import { useQuery, type UseQueryResult } from '@tanstack/react-query';

export interface MfeHealthInfo {
    mfeName?: string;
    mfeVersion: string;
    appVersion: string;
    buildNumber?: string;
    buildTimestamp: string;
    commitHash: string;
}

export interface GlobalHealthResponse {
    status: string;
    appVersion: string;
    appVersionSource: string;
    mfes: Record<string, MfeHealthInfo>;
    environment: string;
}

const fetchGlobalHealth = async (): Promise<GlobalHealthResponse> => {
    // Trailing slash matches `trailingSlash: true` (and the shared Cypress healthcheck),
    // avoiding a redirect hop.
    const response = await fetch('/api/tn/health/');
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    return (await response.json()) as GlobalHealthResponse;
};

/**
 * Dev-only client fetch of the appshell's aggregated `/api/tn/health` endpoint, used by
 * DevTools to surface the global appVersion and the per-MFE version breakdown. Kept off the
 * SSR payload — DevTools only renders inside the non-prod Dev drawer.
 */
export const useGlobalHealth = (): UseQueryResult<GlobalHealthResponse> =>
    useQuery({
        queryKey: ['tn-global-health'],
        queryFn: fetchGlobalHealth,
        enabled: typeof window !== 'undefined',
        staleTime: 30_000,
        retry: false,
    });
